/**
 * Money formatting helpers — the single place every monetary value in the admin
 * panel (E-Wallet, refunds, payouts, credits, balances) is turned into display
 * text.
 *
 * Amounts reach the UI in mixed shapes: MySQL DECIMAL columns arrive as strings
 * ("70.50"), values computed in Node arrive as JS numbers (70.5), and older /
 * unmatched rows arrive as null, undefined or "". Interpolating those directly
 * is what produced the reported defects — "$70.5", "$70", "$ undefined",
 * "$ NaN" — so every amount goes through formatAmount()/formatMoney() and comes
 * out with exactly two decimals and no lost precision.
 */

// Parse anything the API can hand us into a finite Number, keeping the whole
// fractional part. Strings may carry a currency symbol, spaces or thousands
// separators, all of which would otherwise make Number() return NaN.
export const toAmount = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined) return 0;
  const cleaned = String(value).replace(/[^0-9eE.+-]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Round to 2 decimals, half away from zero.
//
// Math.round(value * 100) / 100 rounds the *binary* double rather than the
// decimal the user actually paid: 1.005 is stored as 1.00499999999999989, so it
// would round down to 1.00. Shifting the decimal point through the exponent
// (1.005 -> "1.005e2" -> 100.5) rounds the decimal value instead.
export const roundAmount = (value) => {
  const number = toAmount(value);
  if (!number) return 0;
  const sign = number < 0 ? -1 : 1;
  const [mantissa, exponent = "0"] = Math.abs(number).toExponential().split("e");
  const shifted = Math.round(Number(`${mantissa}e${Number(exponent) + 2}`));
  return (sign * shifted) / 100;
};

// Canonical display form: always exactly two decimals, never truncated.
// 70.5 -> "70.50", "70.50" -> "70.50", 70.505 -> "70.51", null -> "0.00".
export const formatAmount = (value) => roundAmount(value).toFixed(2);

// formatAmount() with a currency symbol in front, e.g. "$ 70.50".
export const formatMoney = (value, symbol = "$", separator = " ") =>
  `${symbol}${separator}${formatAmount(value)}`;

// Add mixed-shape amounts, rounding only once at the end so repeated additions
// cannot accumulate float drift into the displayed cents.
export const sumAmounts = (...values) =>
  roundAmount(values.flat().reduce((total, value) => total + toAmount(value), 0));

export default formatAmount;
